import { useMemo } from 'react';
import { HoverCard, Box } from '@mantine/core';
import type { Participant } from '../../../types';
import type { GroupWithMembers } from '../../../services/api/groups';
import classes from './GroupTitleTooltip.module.css';

interface GroupTitleTooltipProps {
  group: GroupWithMembers;
  participantMap: Record<string, Participant>;
  children: React.ReactNode;
}

export function GroupTitleTooltip({
  group,
  participantMap,
  children,
}: GroupTitleTooltipProps) {
  const demographics = useMemo(() => {
    const members = group.members
      .map((m) => participantMap[m.user_id])
      .filter(Boolean);

    // Personality type breakdown (E/I from MBTI-style types)
    let extraverts = 0;
    let introverts = 0;
    let unknownPersonality = 0;

    members.forEach((p) => {
      if (!p.personality_type) {
        unknownPersonality++;
      } else {
        const first = p.personality_type.charAt(0).toUpperCase();
        if (first === 'E') extraverts++;
        else if (first === 'I') introverts++;
        else unknownPersonality++;
      }
    });

    return {
      members,
      total: members.length,
      extraverts,
      introverts,
      unknownPersonality,
    };
  }, [group.members, participantMap]);

  return (
    <HoverCard
      width="auto"
      position="bottom-start"
      withArrow
      shadow="md"
      openDelay={300}
      closeDelay={100}
    >
      <HoverCard.Target>{children}</HoverCard.Target>
      <HoverCard.Dropdown
        style={{ padding: 0, border: 'none', background: 'transparent' }}
      >
        <Box className={classes.tooltip}>
          {/* Header */}
          <div className={classes.header}>
            <span className={classes.groupName}>{group.name}</span>
            <span className={classes.participantCount}>
              {group.member_count} participant{group.member_count !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Personality Demographics */}
          {demographics.total > 0 && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Personality</div>
              <div className={classes.demographicsRow}>
                <div className={classes.demographicBox}>
                  <div className={classes.demographicValue}>{demographics.extraverts}</div>
                  <div className={classes.demographicLabel}>Extroverts</div>
                </div>
                <div className={classes.demographicBox}>
                  <div className={classes.demographicValue}>{demographics.introverts}</div>
                  <div className={classes.demographicLabel}>Introverts</div>
                </div>
                {demographics.unknownPersonality > 0 && (
                  <div className={classes.demographicBox}>
                    <div className={classes.demographicValue}>{demographics.unknownPersonality}</div>
                    <div className={classes.demographicLabel}>Unknown</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Participant List */}
          <hr className={classes.divider} />
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Participants</div>
            <div className={classes.participantList}>
              {group.members.map((member) => {
                const participant = participantMap[member.user_id];
                const pType = participant?.personality_type?.charAt(0).toUpperCase();
                const personalityLabel = pType === 'E' ? 'Extro' : pType === 'I' ? 'Intro' : null;

                return (
                  <div key={member.user_id} className={classes.participantItem}>
                    <span className={classes.participantName}>
                      {member.first_name} {member.last_name}
                    </span>
                    {personalityLabel && (
                      <span className={`${classes.participantTag} ${pType === 'E' ? classes.tagExtro : classes.tagIntro}`}>
                        {personalityLabel}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Box>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
